<template>
  <div class="tw-flex tw-flex-row tw-space-x-8 tw-w-full">
    <div class="tw-w-10/12">
      <InocuBarChart :series="series" :xaxis="xaxis" :isLoading="isLoading">
        <template #title>
          {{ t('statistics.titles.no_of_requests') }}
        </template>
      </InocuBarChart>
    </div>

    <div class="tw-w-2/12 tw-flex tw-flex-col tw-gap-y-4 tw-max-h-72">
      <InocuCard
        :title="t('statistics.titles.total_order_requests')"
        :value="orderCount"
        :trendingValue="totalOrdersComparison"
      >
        <template #icon>
          <ordersCountIcon />
        </template>
        <template #value>
          {{ formattedOrderCount.value }}
          <span class="tw-text-sm">
            {{ formattedOrderCount.unit }}
          </span>
        </template>
      </InocuCard>

      <InocuCard
        :title="t('statistics.titles.total_payment_requests')"
        :value="paymentCount"
        :trendingValue="totalPaymentsComparison"
        dark
      >
        <template #icon>
          <approvedPaymentVolume />
        </template>
        <template #value>
          {{ formattedPaymentCount.value }}
          <span class="tw-text-sm">
            {{ formattedPaymentCount.unit }}
          </span>
        </template>
      </InocuCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import ordersCountIcon from '../icons/ordersCountIcon.svg';
import approvedPaymentVolume from '../icons/approvedPaymentVolume.svg';
import { ref, onMounted, watch, type Ref, computed } from 'vue';
import InocuBarChart from '@/components/shared/charts/InocuBarChart.vue';
import InocuCard from '@/components/shared/cards/InocuCard.vue';
import { useI18n } from 'vue-i18n';
import { useDashboardStore } from '@/stores/dashboard.store';
import { useFormatValue } from '@/_helpers/composables/formatting.composable';

const { formatLargeNumber } = useFormatValue();

const { getNumberOfRequests } = useDashboardStore();
const props = defineProps<{
  startDate: string;
  endDate: string;
  customerId: number;
  includeRecurring: boolean;
}>();

const isLoading = ref(true);

const { t } = useI18n();

const series: Ref<{ name: string; data: number[] }[]> = ref([
  {
    name: t('statistics.titles.total_payment_requests'),
    data: [],
  },
  {
    name: t('statistics.titles.total_order_requests'),
    data: [],
  },
]);
const xaxis: Ref<string[]> = ref([]);
const orderCount = ref(0);
const paymentCount = ref(0);
const totalOrdersComparison = ref(0);
const totalPaymentsComparison = ref(0);

const formattedOrderCount: Ref<any> = computed(() => {
  return formatLargeNumber(orderCount.value, true);
});
const formattedPaymentCount: Ref<any> = computed(() => {
  return formatLargeNumber(paymentCount.value, true);
});

const fetchData = async () => {
  isLoading.value = true;
  try {
    const data = await getNumberOfRequests(
      props.startDate,
      props.endDate,
      props.customerId,
      props.includeRecurring
    );
    series.value[0].data = data.payments;
    series.value[1].data = data.orders;
    xaxis.value = data.dates;
    orderCount.value = data.total_orders;
    paymentCount.value = data.total_payments;
    totalOrdersComparison.value = data.total_orders_comparison;
    totalPaymentsComparison.value = data.total_payments_comparison;
  } catch (error) {
    console.error('Error fetching number of requests:', error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchData);
watch(() => props, fetchData, { deep: true });
</script>
